import React, { createContext, useContext, useState, useEffect } from 'react';
import { format } from 'date-fns';

const AttendanceContext = createContext();

export const useAttendance = () => {
  const context = useContext(AttendanceContext);
  if (!context) {
    throw new Error('useAttendance must be used within an AttendanceProvider');
  }
  return context;
};

export const AttendanceProvider = ({ children }) => {
  const [students, setStudents] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [currentDate, setCurrentDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // Load data from localStorage on mount
  useEffect(() => {
    const savedStudents = localStorage.getItem('attendanceTracker_students');
    const savedRecords = localStorage.getItem('attendanceTracker_records');
    
    if (savedStudents) {
      try {
        setStudents(JSON.parse(savedStudents));
      } catch (e) {
        console.error('Error loading students:', e);
      }
    }
    
    if (savedRecords) {
      try {
        setAttendanceRecords(JSON.parse(savedRecords));
      } catch (e) {
        console.error('Error loading records:', e);
      }
    }
  }, []);

  // Save to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem('attendanceTracker_students', JSON.stringify(students));
  }, [students]);

  useEffect(() => {
    localStorage.setItem('attendanceTracker_records', JSON.stringify(attendanceRecords));
  }, [attendanceRecords]);

  const addStudent = (name) => {
    if (!name.trim()) return;
    const newStudent = {
      id: Date.now().toString(),
      name: name.trim()
    };
    setStudents(prev => [...prev, newStudent]);
  };

  const addStudentsBulk = (namesText) => {
    const names = namesText
      .split('\n')
      .map(name => name.trim())
      .filter(name => name.length > 0);
    
    const newStudents = names.map(name => ({
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      name
    }));
    
    setStudents(prev => [...prev, ...newStudents]);
  };

  const removeStudent = (studentId) => {
    setStudents(prev => prev.filter(s => s.id !== studentId));
    // Remove from all attendance records
    setAttendanceRecords(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(date => {
        delete updated[date][studentId];
      });
      return updated;
    });
  };

  const editStudent = (studentId, newName) => {
    if (!newName.trim()) return;
    setStudents(prev => 
      prev.map(s => s.id === studentId ? { ...s, name: newName.trim() } : s)
    );
  };

  const markAttendance = (studentId, date, status) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [date]: {
        ...prev[date],
        [studentId]: status
      }
    }));
  };

  const getAttendanceForDate = (date) => {
    return attendanceRecords[date] || {};
  };

  const exportData = () => {
    const data = {
      students,
      attendanceRecords,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `attendance-data-${format(new Date(), 'yyyy-MM-dd-HHmm')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const importData = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          if (data.students && data.attendanceRecords) {
            setStudents(data.students);
            setAttendanceRecords(data.attendanceRecords);
            resolve();
          } else {
            reject(new Error('Invalid file format'));
          }
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const clearAllData = () => {
    setStudents([]);
    setAttendanceRecords({});
    localStorage.removeItem('attendanceTracker_students');
    localStorage.removeItem('attendanceTracker_records');
  };

  const getAnalytics = () => {
    const attendanceDates = Object.keys(attendanceRecords).sort();
    const studentStats = students.map(student => {
      const attendedDates = [];
      const missedDates = [];
      
      attendanceDates.forEach(date => {
        const status = attendanceRecords[date]?.[student.id];
        if (status === 'present') {
          attendedDates.push(date);
        } else if (status === 'absent') {
          missedDates.push(date);
        }
      });
      
      return {
        ...student,
        attendedDates,
        missedDates,
        attendanceRate: attendedDates.length + missedDates.length > 0 
          ? (attendedDates.length / (attendedDates.length + missedDates.length)) * 100 
          : 0
      };
    });

    const dateStats = attendanceDates.map(date => {
      const dayRecords = attendanceRecords[date];
      const present = Object.values(dayRecords).filter(status => status === 'present').length;
      const absent = Object.values(dayRecords).filter(status => status === 'absent').length;
      const total = present + absent;
      
      return {
        date,
        present,
        absent,
        total,
        attendanceRate: total > 0 ? (present / total) * 100 : 0
      };
    });

    return {
      studentStats,
      dateStats,
      totalStudents: students.length,
      totalDays: attendanceDates.length
    };
  };

  return (
    <AttendanceContext.Provider value={{
      students,
      attendanceRecords,
      currentDate,
      setCurrentDate,
      addStudent,
      addStudentsBulk,
      removeStudent,
      editStudent,
      markAttendance,
      getAttendanceForDate,
      exportData,
      importData,
      clearAllData,
      getAnalytics
    }}>
      {children}
    </AttendanceContext.Provider>
  );
};